<template>
  <div class="email-login-form" :class="{ 'standalone-page': isStandalonePage }">
    <h5 v-if="isStandalonePage && mode === 'reset'" class="mb-3 text-center">
      {{ isNewUser ? $t('auth.email_login.create_account_title') : $t('auth.email_login.reset_password_title') }}
    </h5>
    <!-- Login form -->
    <div v-if="mode === 'login'">
      <div class="mb-3">
        <input
          type="email"
          class="form-control"
          placeholder="Email"
          v-model="email"
          @keyup.enter="handleLogin"
          :disabled="loading"
        />
      </div>
      <div class="mb-3">
        <input
          type="password"
          class="form-control"
          :placeholder="$t('auth.email_login.password_placeholder')"
          v-model="password"
          @keyup.enter="handleLogin"
          :disabled="loading"
        />
      </div>
      <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>
      <button
        class="btn btn-primary w-100"
        @click="handleLogin" v-track="{ event: 'auth.login.click', target: 'email' }"
        :disabled="loading || !email || !password"
      >
        <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
        {{ $t('auth.email_login.login_button') }}
      </button>
      <div class="mt-2 d-flex justify-content-between">
        <a href="#" class="small text-muted" @click.prevent="switchMode('forgot')">
          {{ $t('auth.email_login.forgot_password') }}
        </a>
        <a href="#" class="small" @click.prevent="switchMode('register')">
          {{ $t('auth.email_login.register_link') }}
        </a>
      </div>
    </div>

    <!-- Register form -->
    <div v-if="mode === 'register'">
      <p class="small text-muted mb-2">{{ $t('auth.email_login.register_intro') }}</p>
      <div class="mb-3">
        <input
          type="text"
          class="form-control"
          :placeholder="$t('auth.email_login.name_placeholder')"
          v-model="name"
          @keyup.enter="handleRegister"
          :disabled="loading"
        />
      </div>
      <div class="mb-3">
        <input
          type="email"
          class="form-control"
          placeholder="Email"
          v-model="email"
          @keyup.enter="handleRegister"
          :disabled="loading"
        />
      </div>
      <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>
      <button
        class="btn btn-primary w-100"
        @click="handleRegister" v-track="{ event: 'auth.register.click', target: 'email' }"
        :disabled="loading || !email || !name"
      >
        <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
        {{ $t('auth.email_login.get_code_button') }}
      </button>
      <div class="mt-2 text-center">
        <a href="#" class="small text-muted" @click.prevent="switchMode('login')">
          {{ $t('auth.email_login.already_have_account') }}
        </a>
      </div>
    </div>

    <!-- Verify code form -->
    <div v-if="mode === 'verify'">
      <p class="small text-muted mb-2">
        {{ $t('auth.email_login.verify_code_sent') }} <strong>{{ email }}</strong>
      </p>
      <div class="mb-3">
        <input
          type="text"
          class="form-control text-center"
          :placeholder="$t('auth.email_login.code_placeholder')"
          v-model="verifyCode"
          @keyup.enter="handleVerifyCode"
          :disabled="loading"
          maxlength="6"
          inputmode="numeric"
          style="font-size: 20px; letter-spacing: 6px;"
        />
      </div>
      <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>
      <button
        class="btn btn-primary w-100"
        @click="handleVerifyCode"
        :disabled="loading || verifyCode.length !== 6"
      >
        <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
        {{ $t('auth.email_login.confirm_button') }}
      </button>
      <div class="mt-2 text-center">
        <a href="#" class="small text-muted" @click.prevent="handleRegister">
          {{ $t('auth.email_login.resend_code') }}
        </a>
      </div>
    </div>

    <!-- Set password form (after verification or from email link) -->
    <div v-if="mode === 'reset'">
      <p class="small text-muted mb-2">
        {{ isNewUser ? $t('auth.email_login.set_password_new_user') : $t('auth.email_login.set_password_existing') }}
      </p>
      <div class="mb-3">
        <input
          type="password"
          class="form-control"
          :placeholder="$t('auth.email_login.password_placeholder')"
          v-model="password"
          @keyup.enter="handleReset"
          :disabled="loading"
        />
      </div>
      <div class="mb-3">
        <input
          type="password"
          class="form-control"
          :placeholder="$t('auth.email_login.password_confirm_placeholder')"
          v-model="passwordConfirm"
          @keyup.enter="handleReset"
          :disabled="loading"
        />
      </div>
      <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>
      <button
        class="btn btn-primary w-100"
        @click="handleReset"
        :disabled="loading || !password || !passwordConfirm"
      >
        <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
        {{ isNewUser ? $t('auth.email_login.create_account_button') : $t('auth.email_login.set_password_button') }}
      </button>
    </div>

    <!-- Forgot password form -->
    <div v-if="mode === 'forgot'">
      <p class="small text-muted mb-2">
        {{ $t('auth.email_login.forgot_intro') }}
      </p>
      <div class="mb-3">
        <input
          type="email"
          class="form-control"
          placeholder="Email"
          v-model="email"
          @keyup.enter="handleForgot"
          :disabled="loading"
        />
      </div>
      <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>
      <div v-if="success" class="alert alert-success py-2 small">{{ success }}</div>
      <button
        class="btn btn-primary w-100"
        @click="handleForgot"
        :disabled="loading || !email"
      >
        <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
        {{ $t('auth.email_login.send_link_button') }}
      </button>
      <div class="mt-2 text-center">
        <a href="#" class="small text-muted" @click.prevent="switchMode('login')">
          {{ $t('auth.email_login.back_to_login') }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import axiosConfig from '@/lib/axiosConfig';
import { API_URL } from '@/config.js';
import { getAttributionPayload, isTrackingEnabled, whenClientIdReady } from '@/lib/attribution';

export default {
  name: 'EmailLoginForm',
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const { t } = useI18n();
    const isStandalonePage = ['ResetPassword', 'SetPassword'].includes(route.name);
    const mode = ref('login');
    const email = ref('');
    const name = ref('');
    const password = ref('');
    const passwordConfirm = ref('');
    const verifyCode = ref('');
    const error = ref('');
    const success = ref('');
    const loading = ref(false);
    const resetToken = ref('');
    const isNewUser = ref(false);

    onMounted(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      const path = window.location.pathname;

      if (token && (path.includes('reset-password') || path.includes('set-password'))) {
        resetToken.value = token;
        isNewUser.value = path.includes('set-password');
        mode.value = 'reset';
      }
    });

    const switchMode = (newMode) => {
      mode.value = newMode;
      error.value = '';
      success.value = '';
    };

    const handleLogin = async () => {
      if (!email.value || !password.value) return;
      error.value = '';
      loading.value = true;

      try {
        const response = await axiosConfig.post(API_URL, {
          params: {
            action: 'login_email',
            email: email.value,
            password: password.value,
          }
        });

        const data = response.data;
        if (data.success && data.token) {
          localStorage.setItem('authToken', data.token);
          store.commit('user/setAuthentication', true);
          store.dispatch('user/checkUser');
          if (isStandalonePage) router.push('/');
        } else {
          error.value = data.error || t('auth.email_login.error_login');
        }
      } catch (e) {
        error.value = t('auth.email_login.error_connection');
      } finally {
        loading.value = false;
      }
    };

    const handleRegister = async () => {
      if (!email.value || !name.value) return;
      error.value = '';
      loading.value = true;

      // Best-effort attribution capture — web-only registration has no
      // Telegram /start to claim the landing's handoff row, so we send the
      // CID/UTMs here instead. Mirrors userModule.js loginUser() exactly;
      // never blocks or fails registration.
      const configData = store.state.app?.configData || {};
      const chatbotName = configData.chatbot_name || configData.service_name || '';
      let attribution = null;
      if (isTrackingEnabled(chatbotName)) {
        try {
          await whenClientIdReady(2000);
          const attr = getAttributionPayload();
          if (attr && (attr.first_touch || attr.last_touch || attr.ym_client_id)) {
            attribution = attr;
          }
        } catch (e) { /* noop */ }
      }

      try {
        const response = await axiosConfig.post(API_URL, {
          params: {
            action: 'register_email',
            email: email.value,
            name: name.value,
            ...(attribution ? { attribution } : {}),
          }
        });

        const data = response.data;
        if (data.success) {
          isNewUser.value = true;
          mode.value = 'verify';
          verifyCode.value = '';
        } else {
          error.value = data.error || t('auth.email_login.error_register');
        }
      } catch (e) {
        error.value = t('auth.email_login.error_connection');
      } finally {
        loading.value = false;
      }
    };

    const handleVerifyCode = async () => {
      if (verifyCode.value.length !== 6) return;
      error.value = '';
      loading.value = true;

      try {
        const response = await axiosConfig.post(API_URL, {
          params: {
            action: 'verify_register_code',
            email: email.value,
            code: verifyCode.value,
          }
        });

        const data = response.data;
        if (data.success && data.set_password_token) {
          resetToken.value = data.set_password_token;
          mode.value = 'reset';
        } else {
          error.value = data.error || t('auth.email_login.error_invalid_code');
        }
      } catch (e) {
        error.value = t('auth.email_login.error_connection');
      } finally {
        loading.value = false;
      }
    };

    const handleForgot = async () => {
      if (!email.value) return;
      error.value = '';
      success.value = '';
      loading.value = true;

      try {
        await axiosConfig.post(API_URL, {
          params: {
            action: 'request_password_reset',
            email: email.value,
          }
        });

        success.value = t('auth.email_login.reset_link_sent');
      } catch (e) {
        error.value = t('auth.email_login.error_connection');
      } finally {
        loading.value = false;
      }
    };

    const handleReset = async () => {
      if (!password.value || !passwordConfirm.value) return;
      if (password.value !== passwordConfirm.value) {
        error.value = t('auth.email_login.error_password_mismatch');
        return;
      }
      if (password.value.length < 8) {
        error.value = t('auth.email_login.error_password_too_short');
        return;
      }

      error.value = '';
      loading.value = true;

      try {
        const response = await axiosConfig.post(API_URL, {
          params: {
            action: 'reset_password',
            token: resetToken.value,
            new_password: password.value,
          }
        });

        const data = response.data;
        if (data.success) {
          if (data.token) {
            localStorage.setItem('authToken', data.token);
            store.commit('user/setAuthentication', true);
            store.dispatch('user/checkUser');
          }
          // Redirect to home after successful password set
          router.push('/');
        } else {
          error.value = data.error || t('auth.email_login.error_set_password');
        }
      } catch (e) {
        error.value = t('auth.email_login.error_connection');
      } finally {
        loading.value = false;
      }
    };

    return {
      mode,
      email,
      name,
      password,
      passwordConfirm,
      verifyCode,
      error,
      success,
      loading,
      isNewUser,
      isStandalonePage,
      switchMode,
      handleLogin,
      handleRegister,
      handleVerifyCode,
      handleForgot,
      handleReset,
    };
  }
};
</script>

<style scoped>
.email-login-form {
  max-width: 320px;
  margin: 0 auto;
}
.email-login-form.standalone-page {
  max-width: 380px;
  padding-top: 40px;
}
.email-login-form .form-control {
  font-size: 14px;
}
.email-login-form .btn-primary {
  font-size: 14px;
}
</style>
